import * as React from 'react';
import { clsx } from 'clsx';
import { X, ChevronDown, Check } from 'lucide-react';

export type ComboboxOption = {
  value: string;
  label: string;
};

export type ComboboxState = 'default' | 'error' | 'success';

export interface ComboboxProps {
  /**
   * The label for the combobox
   */
  label?: string;
  /**
   * The placeholder text
   */
  placeholder?: string;
  /**
   * The available options
   */
  options: ComboboxOption[];
  /**
   * The selected value
   */
  value?: string;
  /**
   * Callback when value changes
   */
  onChange?: (value: string | undefined) => void;
  /**
   * The validation state
   * @default 'default'
   */
  state?: ComboboxState;
  /**
   * Optional error message
   */
  error?: string;
  /**
   * Optional helper text
   */
  helperText?: string;
  /**
   * Whether the combobox is disabled
   */
  disabled?: boolean;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

const comboboxStyles = {
  base: {
    width: '100%',
    height: '36px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    transition: 'all 0.2s ease-in-out',
    outline: 'none',
    fontSize: '13px',
    padding: '8px',
    paddingRight: '32px',
    margin: '0',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    position: 'relative' as const,
  },
  states: {
    default: {
      border: '1px solid #d1d1d1', // grey-300
      color: '#2f2f2f', // grey-900
    },
    defaultFocus: {
      borderColor: '#3cad51', // brand-500
      outline: '3px solid rgba(60, 173, 81, 0.2)',
    },
    error: {
      border: '1px solid #c93232', // destructive-500
      color: '#2f2f2f',
    },
    errorFocus: {
      borderColor: '#c93232',
      outline: '3px solid rgba(201, 50, 50, 0.2)',
    },
    success: {
      border: '1px solid #16a33d', // success-500
      color: '#2f2f2f',
    },
    successFocus: {
      borderColor: '#16a33d',
      outline: '3px solid rgba(22, 163, 61, 0.2)',
    },
    disabled: {
      backgroundColor: '#f8f8f8', // grey-050
      borderColor: '#efefef', // grey-100
      color: '#7e7e7e', // grey-500
      cursor: 'not-allowed',
    },
  },
};

const labelStyles: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '600',
  color: '#2f2f2f',
  marginBottom: '4px',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const helperTextStyles: React.CSSProperties = {
  fontSize: '13px',
  margin: '0',
  marginTop: '2px',
  color: '#595959',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  lineHeight: '1.4',
};

const errorTextStyles: React.CSSProperties = {
  ...helperTextStyles,
  color: '#a62323', // destructive-600
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
};

const dropdownStyles: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: '0',
  right: '0',
  backgroundColor: '#ffffff',
  border: '1px solid #d1d1d1',
  borderRadius: '8px',
  maxHeight: '200px',
  overflowY: 'auto',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  zIndex: 1000,
};

const optionStyles: React.CSSProperties = {
  padding: '8px 12px',
  fontSize: '13px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  color: '#2f2f2f', // grey-900
};

/**
 * Combobox component - Arbor Design System
 *
 * A searchable select/autocomplete component with filtering.
 * Supports labels, validation states, and helper text.
 */
export const Combobox = React.forwardRef<HTMLDivElement, ComboboxProps>(
  (
    {
      label,
      placeholder = 'default-text',
      options,
      value,
      onChange,
      state = 'default',
      error,
      helperText,
      disabled = false,
      className,
      style,
      'data-testid': dataTestId,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState('');
    const [isFocused, setIsFocused] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const wrapperRef = React.useRef<HTMLDivElement>(null);

    const comboboxId = React.useId();
    const helperTextId = React.useId();
    const errorId = React.useId();

    const selectedOption = options.find((opt) => opt.value === value);

    const filteredOptions = React.useMemo(() => {
      if (!searchQuery) return options;
      return options.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }, [options, searchQuery]);

    // Close dropdown when clicking outside
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          wrapperRef.current &&
          !wrapperRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
          setSearchQuery('');
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
      if (!isOpen) setIsOpen(true);
    };

    const handleSelectOption = (optionValue: string) => {
      onChange?.(optionValue);
      setIsOpen(false);
      setSearchQuery('');
      inputRef.current?.blur();
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange?.(undefined);
      setSearchQuery('');
      inputRef.current?.focus();
    };

    const handleInputFocus = () => {
      setIsFocused(true);
      if (!disabled) setIsOpen(true);
    };

    const handleInputBlur = () => {
      setIsFocused(false);
      // Delay to allow click on option
      setTimeout(() => {
        if (!wrapperRef.current?.contains(document.activeElement)) {
          setSearchQuery('');
        }
      }, 200);
    };

    const currentState = error ? 'error' : state;
    const stateStyles = comboboxStyles.states[currentState];
    const focusStyles =
      currentState === 'error'
        ? comboboxStyles.states.errorFocus
        : currentState === 'success'
        ? comboboxStyles.states.successFocus
        : comboboxStyles.states.defaultFocus;

    const inputWrapperStyle: React.CSSProperties = {
      ...comboboxStyles.base,
      ...stateStyles,
      ...(isFocused && !disabled && focusStyles),
      ...(disabled && comboboxStyles.states.disabled),
    };

    const displayValue = selectedOption ? selectedOption.label : '';
    const showPlaceholder = !selectedOption && !searchQuery && !isFocused;

    return (
      <div className={clsx('arbor-combobox-wrapper', className)} style={style} ref={wrapperRef} data-testid={dataTestId}>
        {label && (
          <label htmlFor={comboboxId} style={labelStyles}>
            {label}
          </label>
        )}

        <div style={{ position: 'relative' }} ref={ref}>
          <div style={inputWrapperStyle}>
            <input
              ref={inputRef}
              id={comboboxId}
              type="text"
              value={searchQuery || displayValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder={showPlaceholder ? placeholder : ''}
              disabled={disabled}
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby={error ? errorId : helperText ? helperTextId : undefined}
              aria-expanded={isOpen}
              aria-autocomplete="list"
              role="combobox"
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                flex: 1,
                fontSize: '13px',
                padding: 0,
                margin: 0,
                color: showPlaceholder ? '#7e7e7e' : '#2f2f2f',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              }}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', position: 'absolute', right: '8px' }}>
              {selectedOption && !disabled && (
                <button
                  type="button"
                  onClick={handleClear}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    padding: '2px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#7e7e7e',
                  }}
                  aria-label="Clear selection"
                >
                  <X size={16} />
                </button>
              )}

              {currentState === 'success' && !isOpen && (
                <Check size={16} color="#16a33d" />
              )}

              <ChevronDown
                size={16}
                color="#7e7e7e"
                style={{
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            </div>
          </div>

          {isOpen && !disabled && (
            <div ref={dropdownRef} style={dropdownStyles}>
              {filteredOptions.length === 0 ? (
                <div style={{ ...optionStyles, color: '#7e7e7e', cursor: 'default' }}>
                  No options found
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => handleSelectOption(option.value)}
                    onMouseDown={(e) => e.preventDefault()} // Prevent blur
                    style={{
                      ...optionStyles,
                      backgroundColor: option.value === value ? '#f8f8f8' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f8f8';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor =
                        option.value === value ? '#f8f8f8' : 'transparent';
                    }}
                  >
                    <span>{option.label}</span>
                    {option.value === value && <Check size={16} color="#16a33d" />}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {error && (
          <p id={errorId} style={errorTextStyles} role="alert">
            {error}
          </p>
        )}
        {!error && helperText && (
          <p id={helperTextId} style={helperTextStyles}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Combobox.displayName = 'Combobox';
